import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  QrCodeIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const fetchBills = (params) => api.get('/bills', { params }).then(res => res.data.data);

export default function Bills() {
  const [filters, setFilters] = useState({ page: 1, limit: 20 });
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading } = useQuery(['bills', filters], () => fetchBills(filters));

  const bills = data?.bills || [];
  const pagination = data?.pagination || {};

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'badge-gray',
      issued: 'badge-blue',
      unpaid: 'badge-yellow',
      paid: 'badge-green',
      blocked: 'badge-red',
      cancelled: 'badge-gray',
      expired: 'badge-gray',
      adjusted: 'badge-blue'
    };
    return styles[status] || 'badge-gray';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bills</h1>
          <p className="mt-1 text-sm text-slate-500">Manage all bills and challans</p>
        </div>
        <Link to="/bills/new" className="btn-primary">
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Bill
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by challan, consumer..."
                className="input-field pl-10"
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <select
                className="input-field"
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="issued">Issued</option>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="blocked">Blocked</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <input
                type="month"
                className="input-field"
                onChange={(e) => setFilters({ ...filters, billingMonth: e.target.value, page: 1 })}
              />
            </div>
          )}
        </div>
      </div>

      {/* Bills Table */}
      <div className="card">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Bill ID</th>
                <th>Challan No</th>
                <th>Consumer</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isLoading ? (
                <tr><td colSpan="7" className="text-center py-8">Loading...</td></tr>
              ) : bills.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-8 text-slate-500">No bills found</td></tr>
              ) : (
                bills.map((bill) => (
                  <tr key={bill.id} className="hover:bg-slate-50">
                    <td className="font-mono text-xs">{bill.billId}</td>
                    <td className="font-medium">{bill.challanNo}</td>
                    <td>{bill.consumerName}</td>
                    <td className="font-medium">
                      PKR {parseFloat(bill.feeBeforeDueDate).toLocaleString()}
                    </td>
                    <td>{new Date(bill.dueDate).toLocaleDateString()}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(bill.status)}`}>
                        {bill.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2 items-center">
                        <Link to={`/bills/${bill.id}`} className="text-indigo-600 hover:text-indigo-900" title="View Details">
                          <DocumentTextIcon className="h-5 w-5" />
                        </Link>
                        {bill.status !== 'paid' && bill.status !== 'cancelled' && (
                          <Link to={`/bills/${bill.id}/edit`} className="text-blue-500 hover:text-blue-800" title="Edit Bill">
                            <PencilIcon className="h-5 w-5" />
                          </Link>
                        )}
                        {bill.qrCode && bill.qrCode.qrImageUrl && (
                          <button
                            className="text-indigo-600 hover:text-indigo-900 hover:scale-110 transition-transform"
                            title="Download QR"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = bill.qrCode.qrImageUrl;
                              link.download = `challan-${bill.challanNo}-qr.png`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              toast.success('Downloading QR Code image');
                            }}
                          >
                            <img src={bill.qrCode.qrImageUrl} alt="QR" className="h-6 w-6 border border-slate-200 rounded-sm" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="card-body border-t border-slate-200 flex justify-between items-center">
            <p className="text-sm text-slate-500">
              Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page <= 1}
                className="btn-secondary disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page >= pagination.totalPages}
                className="btn-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
